import _ from 'underscore';
import {
  getCivilDateDayOfWeek,
  getCivilDateMonthStart,
} from '@bingads-webui-universal/temporal-utitlities';
import { dateRangePreset } from '@bingads-webui-campaign/reports-v4-strings';
import { getAllowedRanges } from './time-grain-range-options';

/**
 * getTimeRanges
 * @param {CivilDate} minSupportDay minimumSupportedDay
 * @param {CivilDate} today today as a civildate
 * @param {string} i18n i18n module
 * @returns {array} - array of date ranges formated for picker
 */
export const getTimeRanges = (minSupportDay, today, i18n) => {
  const daysAwayFromMonday = getCivilDateDayOfWeek(today) ? getCivilDateDayOfWeek(today) - 1 : 6;

  const yesterday = today.plus({ days: -1 });
  const sevenDaysAgo = today.plus({ days: -7 });
  const fourteenDaysAgo = today.plus({ days: -14 });
  const thirtyDaysAgo = today.plus({ days: -30 });
  const thisSunday = today.plus({ days: -getCivilDateDayOfWeek(today) });
  const thisMonday = today.plus({ days: -daysAwayFromMonday });
  const thisMonthFirstDay = getCivilDateMonthStart(today);
  const lastMonthlastDay = thisMonthFirstDay.plus({ days: -1 });
  const lastMonthFirstDay = getCivilDateMonthStart(lastMonthlastDay);
  const lastTwoMonthlastDay = lastMonthFirstDay.plus({ days: -1 });
  const lastTwoMonthFirstDay = getCivilDateMonthStart(lastTwoMonthlastDay);
  const firstDayOfYear = today.with({ day: 1, month: 1 });

  const rawRanges = [
    {
      /* Custom Time Range must have Id = 4 */
      rangeId: 4, key: 'Custom', range: { start: undefined, end: undefined },
    },
    {
      rangeId: 1, key: 'Today', range: { start: today, end: today },
    },
    {
      rangeId: 2, key: 'Yesterday', range: { start: yesterday, end: yesterday },
    },
    {
      rangeId: 3, key: 'LastSevenDays', range: { start: sevenDaysAgo, end: yesterday },
    },
    {
      rangeId: 0, key: 'LastFourteenDays', range: { start: fourteenDaysAgo, end: yesterday },
    },
    {
      rangeId: 5, key: 'LastThirtyDays', range: { start: thirtyDaysAgo, end: yesterday },
    },
    {
      rangeId: 6, key: 'ThisWeekFromSun', range: { start: thisSunday, end: today },
    },
    {
      rangeId: 7, key: 'ThisWeekFromMon', range: { start: thisMonday, end: today },
    },
    {
      rangeId: 8, key: 'LastWeekStartingSun', range: { start: thisSunday.plus({ days: -7 }), end: thisSunday.plus({ days: -1 }) },
    },
    {
      rangeId: 9, key: 'LastWeekStartingMon', range: { start: thisMonday.plus({ days: -7 }), end: thisMonday.plus({ days: -1 }) },
    },
    {
      rangeId: 10, key: 'LastFourWeeksStartingSun', range: { start: thisSunday.plus({ days: -28 }), end: thisSunday.plus({ days: -1 }) },
    },
    {
      rangeId: 11, key: 'LastFourWeeksStartingMon', range: { start: thisMonday.plus({ days: -28 }), end: thisMonday.plus({ days: -1 }) },
    },
    {
      rangeId: 23, key: 'LastSixWeeksStartingSun', range: { start: thisSunday.plus({ days: -42 }), end: thisSunday.plus({ days: -1 }) },
    },
    {
      rangeId: 22, key: 'LastSixWeeksStartingMon', range: { start: thisMonday.plus({ days: -42 }), end: thisMonday.plus({ days: -1 }) },
    },
    {
      rangeId: 12, key: 'ThisMonth', range: { start: thisMonthFirstDay, end: today },
    },
    {
      rangeId: 13, key: 'LastMonth', range: { start: lastMonthFirstDay, end: lastMonthlastDay },
    },
    {
      rangeId: 20, key: 'LastTwoMonths', range: { start: lastTwoMonthFirstDay, end: lastMonthlastDay },
    },
    {
      rangeId: 14, key: 'LastThreeMonths', range: { start: lastMonthFirstDay.plus({ months: -2 }), end: lastMonthlastDay },
    },
    {
      rangeId: 15, key: 'LastSixMonths', range: { start: lastMonthFirstDay.plus({ months: -5 }), end: lastMonthlastDay },
    },
    {
      rangeId: 16, key: 'ThisYear', range: { start: firstDayOfYear, end: today },
    },
    {
      rangeId: 17, key: 'LastYear', range: { start: today.with({ day: 1, month: 1, year: today.year - 1 }), end: firstDayOfYear.plus({ days: -1 }) },
    },
    {
      rangeId: 21, key: 'LastTwoYears', range: { start: today.with({ day: 1, month: 1, year: today.year - 2 }), end: firstDayOfYear.plus({ days: -1 }) },
    },
    {
      rangeId: 18, key: 'AllTime', range: { start: minSupportDay, end: today },
    },
  ];
  const localizedRanges = _.map(rawRanges, (range) => {
    const englishString = dateRangePreset[range.key];
    const localizedString = i18n.getString(englishString);

    return {
      ...range,
      text: localizedString,
    };
  });

  return localizedRanges;
};

/**
 * getFilteredTimeRanges
 * @param {string} timeGrain timeGrain Value
 * @param {CivilDate} today today as a civildate
 * @param {CivilDate} minSupportedDay minSupportedDay as a civildate
 * @param {object} reportSchema schema for the report *
 * @param {string} i18n i18n module
 * @returns {array} - array of date ranges formated for picker
 *  filtered by timeGrain
 */
export const getFilteredTimeRanges = (timeGrain, today, minSupportedDay, reportSchema, i18n) => {
  const specificRanges = getTimeRanges(minSupportedDay, today, i18n);
  const rangeOptions = getAllowedRanges(timeGrain, reportSchema);
  const supportedOptions =
    _.object(rangeOptions, rangeOptions);
  return _.filter(specificRanges, node => _.has(supportedOptions, node.key));
};
